'use client';

import React, { useRef, useEffect, useMemo } from 'react';
import {
  CANONICAL_CHANNELS,
  MappedChannel,
  CompatibilityReport,
  EEGRegion,
} from '../utils/channelAlignment';

// ─── Region colour palette ────────────────────────────────────────────────────
const REGION_COLORS: Record<EEGRegion, { fill: string; glow: string; r: number; g: number; b: number }> = {
  prefrontal: { fill: '#a78bfa', glow: 'rgba(167,139,250,0.5)', r: 167, g: 139, b: 250 },
  frontal:    { fill: '#38bdf8', glow: 'rgba(56,189,248,0.5)',  r: 56,  g: 189, b: 248 },
  central:    { fill: '#34d399', glow: 'rgba(52,211,153,0.5)',  r: 52,  g: 211, b: 153 },
  parietal:   { fill: '#fbbf24', glow: 'rgba(251,191,36,0.5)',  r: 251, g: 191, b: 36  },
  occipital:  { fill: '#f87171', glow: 'rgba(248,113,113,0.5)', r: 248, g: 113, b: 113 },
  temporal:   { fill: '#F97316', glow: 'rgba(249,115,22,0.5)',  r: 249, g: 115, b: 22  },
};

interface TopoMapProps {
  mappedChannels: MappedChannel[];
  report: CompatibilityReport;
  size?: number;
  animated?: boolean;
}

export default function EEGTopoMap({ mappedChannels, report, size = 320, animated = true }: TopoMapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const phaseRef = useRef(0);

  const channelMap = useMemo(() => {
    const map: Record<string, MappedChannel> = {};
    mappedChannels.forEach(ch => { map[ch.id] = ch; });
    return map;
  }, [mappedChannels]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    const cx = size / 2;
    const cy = size / 2;
    const R = size * 0.42;

    const draw = () => {
      if (animated) phaseRef.current += 0.016;
      const phase = phaseRef.current;

      ctx.clearRect(0, 0, size, size);

      // ── Clip to head circle ──
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.clip();

      // Background
      ctx.fillStyle = '#060d1a';
      ctx.fillRect(0, 0, size, size);

      // ── Heatmap blobs for available channels ──
      CANONICAL_CHANNELS.forEach(ch => {
        const mapped = channelMap[ch.id];
        if (!mapped?.available) return;

        const ex = ch.x * size;
        const ey = ch.y * size;
        const activity = mapped.signalQuality / 100;
        const pulse = animated ? 0.85 + Math.sin(phase + ch.x * 3 + ch.y * 2) * 0.15 : 1;
        const finalActivity = Math.min(1, activity * pulse);
        const blobR = R * (0.18 + finalActivity * 0.22);
        const col = REGION_COLORS[ch.region];

        const grad = ctx.createRadialGradient(ex, ey, 0, ex, ey, blobR);
        grad.addColorStop(0, `rgba(${col.r},${col.g},${col.b},${0.4 * finalActivity})`);
        grad.addColorStop(1, `rgba(${col.r},${col.g},${col.b},0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(ex, ey, blobR, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.restore();

      // ── Head outline ──
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(30,58,95,0.9)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // ── Nose ──
      ctx.beginPath();
      ctx.moveTo(cx - 8, cy - R + 4);
      ctx.lineTo(cx, cy - R - 10);
      ctx.lineTo(cx + 8, cy - R + 4);
      ctx.strokeStyle = 'rgba(30,58,95,0.7)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // ── Ears ──
      [[-1], [1]].forEach(([dir]) => {
        ctx.beginPath();
        ctx.arc(cx + dir * (R + 4), cy, 7, Math.PI * 0.4, Math.PI * 1.6, dir === -1);
        ctx.strokeStyle = 'rgba(30,58,95,0.7)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      });

      // ── Electrode dots ──
      CANONICAL_CHANNELS.forEach(ch => {
        const mapped = channelMap[ch.id];
        const ex = ch.x * size;
        const ey = ch.y * size;
        const col = REGION_COLORS[ch.region];

        if (!mapped?.available) {
          // Unavailable — grey ghost dot
          ctx.beginPath();
          ctx.arc(ex, ey, 3, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(60,70,90,0.5)';
          ctx.fill();
          ctx.strokeStyle = 'rgba(60,70,90,0.3)';
          ctx.lineWidth = 0.5;
          ctx.stroke();

          // Dashed X mark for critical missing
          if (ch.isCritical) {
            ctx.strokeStyle = 'rgba(239,68,68,0.4)';
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(ex - 3, ey - 3); ctx.lineTo(ex + 3, ey + 3);
            ctx.moveTo(ex + 3, ey - 3); ctx.lineTo(ex - 3, ey + 3);
            ctx.stroke();
          }
          return;
        }

        const activity = mapped.signalQuality / 100;
        const pulse = animated ? 0.85 + Math.sin(phase * 1.4 + ch.x * 4 + ch.y * 3) * 0.15 : 1;
        const finalActivity = Math.min(1, activity * pulse);
        const dotR = 3.5 + finalActivity * 3;

        // Glow ring
        ctx.beginPath();
        ctx.arc(ex, ey, dotR + 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${col.r},${col.g},${col.b},${0.2 * finalActivity})`;
        ctx.fill();

        // Dot
        ctx.beginPath();
        ctx.arc(ex, ey, dotR, 0, Math.PI * 2);
        const statusAlpha = mapped.status === 'poor' ? 0.4 : mapped.status === 'fair' ? 0.7 : 1;
        ctx.fillStyle = `rgba(${col.r},${col.g},${col.b},${statusAlpha})`;
        ctx.fill();

        // Label
        ctx.font = `bold ${8 + finalActivity * 2}px monospace`;
        ctx.fillStyle = `rgba(255,255,255,${0.4 + finalActivity * 0.5})`;
        ctx.textAlign = 'center';
        ctx.fillText(ch.label, ex, ey - dotR - 3);
      });

      if (animated) {
        animRef.current = requestAnimationFrame(draw);
      }
    };

    draw();
    return () => cancelAnimationFrame(animRef.current);
  }, [mappedChannels, size, animated, channelMap]);

  const isFullRes = report.resolutionMode === 'full';

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Resolution badge */}
      <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold border ${
        isFullRes
          ? 'bg-emerald-950/40 border-emerald-800/50 text-emerald-400' :'bg-amber-950/40 border-amber-800/50 text-amber-400'
      }`}>
        <span className={`w-1.5 h-1.5 rounded-full ${isFullRes ? 'bg-emerald-400' : 'bg-amber-400'}`} />
        {isFullRes ? 'Full-resolution topomap' : 'Sparse topomap (reduced coverage)'}
      </div>

      {/* Canvas */}
      <div className="relative">
        <canvas ref={canvasRef} style={{ borderRadius: '50%' }} />
        {/* Overlay label for reduced mode */}
        {!isFullRes && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[9px] text-amber-400/70 font-mono whitespace-nowrap">
            {report.availableChannels} / {CANONICAL_CHANNELS.length} channels
          </div>
        )}
      </div>

      {/* Region legend */}
      <div className="grid grid-cols-3 gap-x-4 gap-y-1.5 w-full max-w-[280px]">
        {(Object.entries(REGION_COLORS) as [EEGRegion, typeof REGION_COLORS[EEGRegion]][]).map(([region, col]) => (
          <div key={region} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: col.fill }} />
            <span className="text-[10px] text-zinc-400 capitalize">{region}</span>
          </div>
        ))}
      </div>

      {/* Unavailable channel legend */}
      <div className="flex items-center gap-3 text-[10px] text-zinc-500">
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-zinc-700" />
          <span>Unavailable</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-red-400 font-mono text-[9px]">✕</span>
          <span>Critical missing</span>
        </div>
      </div>
    </div>
  );
}
